import { motion } from "framer-motion";

// Line-by-line masked reveal for editorial headings
export function RevealLine({ children, delay = 0, className = "" }) {
    return (
        <span className={`reveal-mask ${className}`}>
            <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.05, ease: [0.65, 0, 0.35, 1], delay }}
                style={{ display: "inline-block" }}
            >
                {children}
            </motion.span>
        </span>
    );
}

// In-view masked reveal (for below-the-fold headings)
export function RevealLineInView({ children, delay = 0, className = "" }) {
    return (
        <span className={`reveal-mask ${className}`}>
            <motion.span
                initial={{ y: "100%", opacity: 0 }}
                whileInView={{ y: "0%", opacity: 1 }}
                viewport={{ once: true, amount: 0 }}
                transition={{ duration: 1, ease: [0.65, 0, 0.35, 1], delay }}
                style={{ display: "inline-block" }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export function FadeUp({ children, delay = 0, y = 24, className = "", once = true }) {
    return (
        <motion.div
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once, amount: 0.3 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

export function ScaleImage({ src, alt, className = "" }) {
    return (
        <div className={`overflow-hidden ${className}`}>
            <motion.img
                src={src}
                alt={alt}
                loading="lazy"
                initial={{ scale: 1.15, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true, amount: 0.25 }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="w-full h-full object-cover"
            />
        </div>
    );
}

// Shutter-style reveal: a cream overlay slides up revealing the image while it gently zooms out.
// Pair with a parent that already defines size + overflow-hidden if you want a cropped container.
export function RevealImage({ src, alt, className = "", delay = 0, zoom = true }) {
    return (
        <div className={`relative overflow-hidden ${className}`}>
            {/* The image is always rendered — zoom-out on scroll into view */}
            <motion.img
                src={src}
                alt={alt}
                loading="lazy"
                initial={zoom ? { scale: 1.22 } : false}
                whileInView={zoom ? { scale: 1 } : undefined}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1], delay }}
                className="w-full h-full object-cover"
            />
            {/* Cream mask overlay slides UP to reveal the image */}
            <motion.div
                aria-hidden="true"
                initial={{ y: "0%" }}
                whileInView={{ y: "-101%" }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1], delay }}
                className="absolute inset-0 bg-cream"
            />
            {/* Thin gold line that trails the shutter edge */}
            <motion.span
                aria-hidden="true"
                initial={{ y: "0%", scaleX: 0 }}
                whileInView={{ y: "-101vh", scaleX: 1 }}
                viewport={{ once: true, amount: 0.12 }}
                transition={{
                    y: { duration: 1.3, ease: [0.76, 0, 0.24, 1], delay },
                    scaleX: { duration: 0.6, ease: [0.65, 0, 0.35, 1], delay },
                }}
                style={{ transformOrigin: "left center" }}
                className="pointer-events-none absolute left-0 right-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent z-[2]"
            />
        </div>
    );
}
